<template>
  <div class="pa-4">
    <a @click="backToOverview">
      <v-icon color="white">mdi-chevron-left</v-icon>
      <span class="overview-back-btn-label">{{
        $t("map.tolldomains.actions.back_to_overview")
      }}</span>
    </a>
    <div class="ma-4 overview-title">
      {{ newVariant ? $t("map.tolldomains.actions.add_variant_config") : $t("map.tolldomains.actions.add_period") }}
    </div>
    <v-stepper v-model="currentStepIndex" alt-labels dark>
      <v-stepper-header class="mb-7" dark>
        <template v-for="(title, index) in steps">
          <v-stepper-step
            :key="`step-${index}`"
            :step="index + 1"
            @click="navigate(index)"
          >
            <span>{{ $t(title) }}</span>
          </v-stepper-step>
          <v-divider v-if="index + 1 !== steps.length" :key="index + 1" />
        </template>
      </v-stepper-header>
      <v-stepper-items>
        <v-stepper-content
          v-for="(title, index) in steps"
          :key="`content-step-${index}`"
          :step="index + 1"
          class="pa-0"
        >
          <OverviewGeneral
            v-if="index === 0"
            :loading="loading"
            :index="index"
            @updateModel="setModel"
            @updateError="setError"
          />
          <OverviewExistingZones
            v-if="index === 1 && mapExistingLoaded"
            :index="index"
            :mapLoaded="mapExistingLoaded"
            :zones="newPeriod"
          />
          <OverviewNewZones
            v-if="index === 2 && mapNewLoaded"
            :mapLoaded="mapNewLoaded"
            :index="index"
            :zones="newPeriod"
            @updateZones="setZones"
          />
          <OverviewMissingZones
            v-if="index === 3 && mapMissingLoaded"
            :mapLoaded="mapMissingLoaded"
            :index="index"
            :zones="newPeriod"
          />
          <div v-if="index === 4 && mapConfirmationLoaded">
            <OverviewConfirmation
              :key="ConfirmationKey"
              :mapLoaded="mapConfirmationLoaded"
              :index="index"
              :zones="newPeriod"
              :loading="loading"
              :periodCreated="periodCreated"
              :newVariant="newVariant"
              @updateModel="setModel"
              @updateError="setError"
            />
          </div>
          <div class="actions pt-9">
            <v-btn
              v-if="index + 1 === 1 && !periodCreated"
              dark
              dense
              rounded
              text
              class="cancel"
              @click="backToOverview"
            >
              {{ $t("common.cancel") }}
            </v-btn>
            <v-btn
              v-else-if="!periodCreated"
              dark
              dense
              rounded
              text
              class="back"
              @click="navigate(index - 1)"
            >
              {{ $t("common.back") }}
            </v-btn>
            <v-btn
              v-if="index + 1 === steps.length && !periodCreated"
              dark
              dense
              rounded
              text
              class="save"
              :disabled="preventConfirm(index)"
              @click="save"
            >
              {{ $t("common.confirm") }}
            </v-btn>
            <v-btn
              v-else-if="!periodCreated"
              id="overview-continue-btn"
              dark
              dense
              rounded
              text
              class="continue"
              :class="{ blocked: preventContinue(index) }"
              :disabled="preventContinue(index)"
              @click="navigate(index + 1)"
            >
              {{ $t("common.continue") }}
            </v-btn>
          </div>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
    <Notification
      :content="notificationContent"
      :type="notificationStatus"
      :isNotificationOpen="isNotificationOpen"
      @notificationClosed="closeNotification"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import Notification from '@/components/common/Notification/Notification.vue';
import { min } from 'lodash';
import moment from 'moment';
import OverviewGeneral from '@/views/TollDomains/Domains/components/Overview/OverviewGeneral.vue';
import { store } from '@/store/modules/tollDomain/types';
import OverviewExistingZones from '@/views/TollDomains/Domains/components/Overview/OverviewExistingZones.vue';
import OverviewNewZones from '@/views/TollDomains/Domains/components/Overview/OverviewNewZones.vue';
import OverviewMissingZones from '@/views/TollDomains/Domains/components/Overview/OverviewMissingZones.vue';
import OverviewConfirmation from '@/views/TollDomains/Domains/components/Overview/OverviewConfirmation.vue';
import './styles/overview-new-period-stepper.scss';

@Component({
  components: {
    OverviewGeneral,
    Notification,
    OverviewExistingZones,
    OverviewNewZones,
    OverviewMissingZones,
    OverviewConfirmation
  }
})
export default class OverviewNewPeriodStepper extends Vue {
  @store.Action
  private createGeometry!: (payload: any) => any;

  @store.Action
  private createVariant!: (payload: any) => any;

  @store.Action
  private fetchGeometryDiff!: (payload: any) => any;

  @store.Action
  private activateGeometry!: (payload: any) => any;

  private loading = false;

  private periodCreated = false;

  private isNotificationOpen = false;

  private notificationContent = '';

  private notificationStatus = '';

  private currentStepIndex = 1;

  private currentGeoZipUploaded: File | null = null;

  private steps = [
    'map.tolldomains.overview.steps.map_and_period',
    'map.tolldomains.overview.steps.existing_zones',
    'map.tolldomains.overview.steps.new_zones',
    'map.tolldomains.overview.steps.missing_zones',
    'map.tolldomains.overview.steps.confirmation'
  ];

  // A unique number to update the confirmation component when going back to step 3
  private ConfirmationKey = Date.now();

  private stepErrors = [true, false, false, false, false];

  private mapNewLoaded = false;

  private mapExistingLoaded = false;

  private mapMissingLoaded = false;

  private mapConfirmationLoaded = false;

  private backToOverview() {
    this.$router.push({
      name: 'tolldomains-overview'
    });
  }

  // model for GEOZip upload or variant choice
  private models: any = {};

  // model for the new period creation
  private newPeriod = {
    geometryInfo: { geometryId: 0, geometryName: '', startTs: 0 },
    variantId: 0,
    masterId: 0,
    zonesChanges: {
      existing: [],
      add: [],
      missing: []
    }
  };

  // Whether we redirect from /overview to create a new variant
  private newVariant = false;

  // Whether we redirect from /overview to update the map on an existing variant
  private selectedVariant = 0;

  mounted() {
    if (this.$route.params.newVariant) {
      this.newVariant = true;
    } else if (this.$route.params.selectedVariant && Number(this.$route.params.selectedVariant) > 0) {
      this.selectedVariant = Number(this.$route.params.selectedVariant);
    }
  }

  private async navigate(index: number) {
    const indexError = this.stepErrors.indexOf(true);
    if (index === 1) {
      this.loading = true;
      const response = await this.uploadGeometry(this.models);
      // In case of upload error
      if (response.message) {
        this.isNotificationOpen = true;
        this.notificationContent = `${response.message.split(':')[0]}`;
        this.notificationStatus = 'error';
        this.loading = false;
        return;
      }
      this.newPeriod = response;
      this.newPeriod.variantId = this.selectedVariant;
      this.newPeriod.geometryInfo.startTs = this.models.period.startTs;
      this.newPeriod.masterId = this.models.period.masterId;
      this.loading = false;
      this.mapExistingLoaded = true;
    } else if (index === 2) {
      // CTP-8575 Force reactivity
      // Going back 1 step and again to step 3, zonesChanges.add is not modified
      const addZones = this.newPeriod.zonesChanges.add;
      this.newPeriod.zonesChanges.add = [];
      addZones.forEach((zone: any) => {
        zone.name = zone.code.length === 2 ? this.getCountryFromCode(zone.code) : zone.code;
        zone.providerId = zone.providerId ? zone.providerId : 0;
      });
      this.newPeriod.zonesChanges.add = addZones;
      this.mapNewLoaded = true;
    } else if (index === 3) {
      this.mapMissingLoaded = true;
    } else if (index === 4) {
      // When going back in the steps, Step 5 (confirmation) is not affected by v-if so the model is not updated
      // We force the update by changing it's key value (updates the prop :zones=newPeriod)
      this.ConfirmationKey = Date.now();
      this.mapConfirmationLoaded = true;
    }
    this.currentStepIndex = indexError >= 0
      ? min([index + 1, indexError + 1])!
      : min([index + 1, this.steps.length])!;
  }

  private setModel(index: number, model: any) {
    this.$set(this.models, 'period', model);
    if (model.useVariant) {
      this.selectedVariant = model.variantId;
    }
  }

  /**
   * Change new zones names from Step 3
   */
  private setZones(zonesChanges: any) {
    this.$set(this.newPeriod, 'zonesChanges', zonesChanges);
  }

  private setError(index: number, error: boolean) {
    this.$set(this.stepErrors, index, error);
  }

  private preventContinue(index: number) {
    return this.stepErrors[index];
  }

  private preventConfirm(index: number) {
    return this.stepErrors[index];
  }

  private async uploadGeometry(models: any) {
    // Use variant
    if (models.period.useVariant && models.period.currentGeometry) {
      const newGeo = await this.fetchGeometryDiff({ masterId: models.period.masterId, geometryId: models.period.currentGeometry, variantId: models.variantId });
      newGeo.zonesChanges.add = [...newGeo.zonesChanges.existing];
      newGeo.zonesChanges.existing = [];
      return newGeo;
    }
    // Update map
    if (
      !this.currentGeoZipUploaded
      || this.currentGeoZipUploaded.name !== models.period.geoZip.name
    ) {
      models.period.startTs = moment(models.period.startTs).unix();
      const response = await this.createGeometry(models.period);
      if (response.message) {
        return response;
      }
      return this.fetchGeometryDiff({ masterId: models.period.masterId, geometryId: response, variantId: this.selectedVariant });
    }
    return false;
  }

  private getCountryFromCode(code: string) {
    const regionNames = new Intl.DisplayNames([this.$i18n.locale], {
      type: 'region'
    });

    return regionNames.of(code);
  }

  private async save() {
    this.loading = true;
    this.newPeriod.geometryInfo.geometryName = this.models.period.geometryName;
    this.newPeriod.geometryInfo.startTs = moment(this.models.period.startTs).unix();
    // Update map of existing variant
    if (this.selectedVariant > 0) {
      this.newPeriod.variantId = this.selectedVariant;
    }
    // Case new variant, existing[] should be empty and add[] should contain old existing zones
    if (this.newVariant) {
      const response = await this.createVariant(this.models.period.variantName);
      if (response.message) {
        this.isNotificationOpen = true;
        this.notificationContent = `${response.message.split(':')[0]}`;
        this.notificationStatus = 'error';
        this.loading = false;
        this.periodCreated = false;
        return;
      }
      this.newPeriod.variantId = response;
      this.newPeriod.zonesChanges.existing = [];
      this.newPeriod.zonesChanges.add.forEach((z: any) => { delete z.endTs; z.id = 0; });
    }
    const response = await this.activateGeometry(this.newPeriod);
    // In case of API error
    if (response.message) {
      this.isNotificationOpen = true;
      this.notificationContent = `${response.message.split(':')[0]}`;
      this.notificationStatus = 'error';
      this.loading = false;
      this.periodCreated = false;
      return;
    }
    this.loading = false;
    this.periodCreated = true;
  }

  private closeNotification() {
    this.isNotificationOpen = false;
    this.notificationContent = '';
    this.notificationStatus = '';
  }
}
</script>
